import React, { useState, useEffect, useCallback } from 'react';
import { useAuth } from '../context/AuthContext';
import { useI18n } from '../context/I18nContext';
import './TopVehicleModels.css';

function TopVehicleModels() {
  const { token, hasPermission, apiFetch } = useAuth();
  const { language } = useI18n();
  
  const [view, setView] = useState('top50');
  const [customLimit, setCustomLimit] = useState('');
  const [searchLimit, setSearchLimit] = useState('');
  const [vehicles, setVehicles] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  const t = language === 'da'
    ? {
        topVehicles: 'Populære køretøjer',
        topVehiclesSubtitle: 'Se hvilke køretøjer der er mest populære på serveren',
        loadingVehicles: 'Indlæser køretøjsdata...',
        rank: 'Rang',
        model: 'Model / Spawn kode',
        name: 'Navn',
        count: 'Antal',
        percentage: 'Andel',
        custom: 'Brugerdefineret',
        customPlaceholder: 'Indtast antal (f.eks. 25)',
        apply: 'Anvend',
        failedLoad: 'Kunne ikke indlæse køretøjsdata',
        noVehicles: 'Ingen køretøjer fundet',
        viewTop25: 'Top 25',
        viewTop50: 'Top 50',
        viewTop100: 'Top 100',
        noAccess: 'Du har ikke tilladelse til at se denne side'
      }
    : {
        topVehicles: 'Popular Vehicle Models',
        topVehiclesSubtitle: 'See which vehicles are most popular on the server',
        loadingVehicles: 'Loading vehicle data...',
        rank: 'Rank',
        model: 'Model / Spawn Code',
        name: 'Name',
        count: 'Count',
        percentage: 'Share',
        custom: 'Custom',
        customPlaceholder: 'Enter amount (e.g. 25)',
        apply: 'Apply',
        failedLoad: 'Failed to load vehicle data',
        noVehicles: 'No vehicles found',
        viewTop25: 'Top 25',
        viewTop50: 'Top 50',
        viewTop100: 'Top 100',
        noAccess: 'You do not have permission to view this page'
      };

  const canView = hasPermission('vehicles.view');

  const handleCustomSearch = () => {
    if (customLimit && customLimit.trim()) {
      setSearchLimit(customLimit);
      setView('custom');
    }
  };

  const handleCustomKeyPress = (e) => {
    if (e.key === 'Enter') {
      handleCustomSearch();
    }
  };

  const fetchVehicles = useCallback(async () => {
    if (!token) {
      console.log('No token available yet');
      return;
    }
    
    setLoading(true);
    setError('');
    try {
      let endpoint;
      
      if (view === 'custom' && searchLimit) {
        const limit = Math.min(Math.max(parseInt(searchLimit) || 50, 1), 500);
        endpoint = `/api/vehicles/models/top?limit=${limit}`;
      } else if (view === 'top25') {
        endpoint = '/api/vehicles/models/top?limit=25';
      } else if (view === 'top50') {
        endpoint = '/api/vehicles/models/top?limit=50';
      } else if (view === 'top100') {
        endpoint = '/api/vehicles/models/top?limit=100';
      } else {
        endpoint = '/api/vehicles/models/top?limit=50';
      }
      
      console.log('Fetching from:', endpoint);
      const response = await apiFetch(endpoint);
      console.log('Response status:', response.status);
      
      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.error || t.failedLoad);
      }
      
      const data = await response.json();
      console.log('Received data:', data);
      setVehicles(data.models || []);
    } catch (err) {
      console.error('Error fetching vehicle models:', err);
      setError(err.message || t.failedLoad);
      setVehicles([]);
    } finally {
      setLoading(false);
    }
  }, [view, searchLimit, t.failedLoad, apiFetch, token]);

  useEffect(() => {
    if (canView) {
      fetchVehicles();
    }
  }, [view, searchLimit, canView, fetchVehicles]);

  if (!canView) {
    return <div className="top-vehicles-page"><div className="error-message">{t.noAccess}</div></div>;
  }

  const totalCount = vehicles.reduce((sum, v) => sum + (v.count || 0), 0);

  return (
    <div className="top-vehicles-page">
      <div className="lookup-header">
        <h2>{t.topVehicles}</h2>
        <p>{t.topVehiclesSubtitle}</p>
      </div>

      <div className="view-controls">
        <div className="button-group">
          <button
            className={view === 'top25' ? 'active' : ''}
            onClick={() => setView('top25')}
            disabled={loading}
          >
            {t.viewTop25}
          </button>
          <button
            className={view === 'top50' ? 'active' : ''}
            onClick={() => setView('top50')}
            disabled={loading}
          >
            {t.viewTop50}
          </button>
          <button
            className={view === 'top100' ? 'active' : ''}
            onClick={() => setView('top100')}
            disabled={loading}
          >
            {t.viewTop100}
          </button>
        </div>

        <div className="custom-search">
          <input
            type="number"
            min="1"
            max="500"
            placeholder={t.customPlaceholder}
            value={customLimit}
            onChange={(e) => setCustomLimit(e.target.value)}
            onKeyPress={handleCustomKeyPress}
            disabled={loading}
            className="search-input"
          />
          <button
            onClick={handleCustomSearch}
            disabled={loading || !customLimit}
            className="search-button"
          >
            {t.apply}
          </button>
        </div>
      </div>

      {loading && <div className="loading-message">{t.loadingVehicles}</div>}
      {error && <div className="error-message"><strong>{error}</strong></div>}

      {!loading && vehicles.length > 0 && (
        <div className="results-section">
          <div className="vehicles-table-container">
            <table className="data-table">
              <thead>
                <tr>
                  <th className="rank-col">{t.rank}</th>
                  <th className="model-col">{t.model}</th>
                  <th className="count-col">{t.count}</th>
                  <th className="percentage-col">{t.percentage}</th>
                </tr>
              </thead>
              <tbody>
                {vehicles.map((vehicle, index) => (
                  <tr key={vehicle.model} className={index < 3 ? `top-${index + 1}` : ''}>
                    <td className="rank-cell">#{index + 1}{index < 3 && <span className="medal"> [+]</span>}</td>
                    <td className="model-cell mono">{vehicle.model}</td>
                    <td className="count-cell">{vehicle.count || 0}</td>
                    <td className="percentage-cell">
                      {totalCount > 0 ? ((vehicle.count / totalCount) * 100).toFixed(2) : 0}%
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {!loading && vehicles.length === 0 && !error && (
        <div className="no-data">{t.noVehicles}</div>
      )}
    </div>
  );
}

export default TopVehicleModels;
